import React, { useEffect, useState } from 'react';
import { LuCircleAlert, LuMapPin, LuArrowRight, LuShieldCheck } from "react-icons/lu";
import { db } from '../utils/firebase';
import { collection, doc, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNavigate, Link } from 'react-router-dom';

export default function Checkout() {
  const { user }    = useAuth();
  const { cart, getTotalPrice, clearCart } = useCart();
  const navigate    = useNavigate();

  // Block unverified users
  if (user && !user.emailVerified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="bg-card rounded-2xl border border-border shadow-sm p-10 max-w-md text-center">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/50 flex items-center justify-center mx-auto mb-4">
            <LuCircleAlert size={28} className="text-amber-500" />
          </div>
          <h2 className="text-xl font-black text-foreground mb-2">Email Not Verified</h2>
          <p className="text-[var(--color-muted-foreground)] text-sm mb-6">
            Please verify your email address before placing an order. Check your inbox for a verification link.
          </p>
          <Link to="/" className="inline-block bg-brand-navy hover:bg-brand-amber transition-colors text-white font-bold px-6 py-3 rounded-xl text-sm">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const [addresses,        setAddresses]        = useState([]);
  const [selectedAddress,  setSelectedAddress]  = useState('');
  const [error,            setError]            = useState('');
  const [loading,          setLoading]          = useState(false);
  const [verifiedItems,    setVerifiedItems]    = useState([]);
  const [verifiedTotal,    setVerifiedTotal]    = useState(null);

  useEffect(() => {
    if (cart.length === 0) { navigate('/cart'); return; }
    if (!user) return;
    (async () => {
      try {
        const [userSnap, ...productDocs] = await Promise.all([
          getDoc(doc(db, 'users', user.uid)),
          ...cart.map(i => getDoc(doc(db, 'products', i.id))),
        ]);

        if (userSnap.exists()) {
          const d = userSnap.data();
          setAddresses([{ id: user.uid, street: d.address, city: d.city, state: d.state, zip: d.zip }]);
          setSelectedAddress(user.uid);
        }

        const items = cart.map((cartItem, idx) => {
          const serverData = productDocs[idx].data();
          return {
            productId: cartItem.id,
            name: cartItem.name,
            quantity: cartItem.quantity,
            selectedVariant: cartItem.selectedVariant || null,
            price: serverData?.price ?? cartItem.price,
            cartKey: cartItem.cartKey,
          };
        });
        setVerifiedItems(items);
        setVerifiedTotal(items.reduce((sum, item) => sum + item.price * item.quantity, 0));
      } catch { setError('Failed to load checkout details.'); }
    })();
  }, [user, cart.length]); // eslint-disable-line

  const handlePlaceOrder = async () => {
    if (!user?.uid) return;
    if (!selectedAddress) { setError('Please select a shipping address.'); return; }
    if (verifiedTotal === null) { setError('Prices are still loading. Please wait a moment.'); return; }
    setLoading(true);
    try {
      const selected = addresses.find(a => a.id === selectedAddress);
      const ref = await addDoc(collection(db, 'orders'), {
        userId: user.uid,
        items: verifiedItems.map(({ cartKey: _ck, ...rest }) => rest),
        total: verifiedTotal,
        shippingAddress: selected,
        createdAt: serverTimestamp(),
        status: 'Processing',
      });
      clearCart();
      navigate("/order-confirmation", { state: { orderId: ref.id } });
    } catch { setError('Failed to place order. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background font-sans">
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-widest mb-8">
          <Link to="/cart" className="hover:text-foreground transition-colors">Cart</Link>
          <span>/</span>
          <span className="text-brand-amber">Checkout</span>
        </nav>

        <h1 className="text-3xl font-black text-foreground tracking-tight mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Left — shipping + confirm */}
          <div className="lg:col-span-2 space-y-6">
            {/* Shipping address */}
            <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
              <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
                <LuMapPin size={16} className="text-brand-amber" />
                Shipping Address
              </h3>
              {addresses.length === 0 ? (
                <p className="text-sm text-[var(--color-muted-foreground)]">No address saved. <Link to="/shipping-address" className="text-brand-amber font-bold hover:underline">Add one here →</Link></p>
              ) : (
                <div className="space-y-3">
                  {addresses.map(addr => (
                    <label key={addr.id} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${selectedAddress === addr.id ? "border-brand-navy dark:border-accent bg-accent/10" : "border-border hover:border-ring"}`}>
                      <input type="radio" name="address" value={addr.id} checked={selectedAddress === addr.id} onChange={() => setSelectedAddress(addr.id)} className="mt-0.5 accent-brand-navy" />
                      <span className="text-sm text-foreground font-medium">
                        {addr.street}, {addr.city}, {addr.state} {addr.zip}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {error && <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-950/30 dark:border-red-900/50 dark:text-red-400 text-sm px-4 py-3 rounded-xl">{error}</div>}

            <button
              onClick={handlePlaceOrder}
              disabled={loading || cart.length === 0 || verifiedTotal === null}
              className="w-full bg-brand-navy hover:bg-brand-amber disabled:opacity-50 transition-colors text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2"
            >
              {loading ? "Placing Order…" : (
                <>
                  Place Order · {verifiedTotal !== null ? `$${verifiedTotal.toFixed(2)}` : '…'}
                  <LuArrowRight size={20} />
                </>
              )}
            </button>
          </div>

          {/* Right — order summary */}
          <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
            <h3 className="font-bold text-foreground mb-4">Order Summary</h3>
            <div className="space-y-3 mb-5">
              {(verifiedItems.length > 0 ? verifiedItems : cart).map((item, i) => (
                <div key={i} className="flex justify-between items-start text-sm gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{item.name}</p>
                    {item.selectedVariant && <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5">{item.selectedVariant}</p>}
                    <p className="text-xs text-[var(--color-muted-foreground)]">× {item.quantity}</p>
                  </div>
                  <span className="font-bold text-foreground flex-shrink-0">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4 flex justify-between">
              <span className="font-black text-foreground">Total</span>
              <span className="font-black text-foreground text-lg">
                {verifiedTotal !== null ? `$${verifiedTotal.toFixed(2)}` : '…'}
              </span>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
              <LuShieldCheck size={16} className="text-green-500 flex-shrink-0" />
              Secure wholesale checkout
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
